import { ApolloClient, ApolloLink } from "@apollo/client";
import { Defer20220824Handler } from "@apollo/client/incremental";
import { ClientAwarenessLink } from "@apollo/client/link/client-awareness";
import { SetContextLink } from "@apollo/client/link/context";
import { BaseHttpLink } from "@apollo/client/link/http";
import { getAccessTokenSilently } from "@shuttlerock/auth";
import { ConfigService } from "@shuttlerock/configuration";
import { createCache } from "./cache";
import { fenceAtLeastAdapterLink } from "./fence-atleast-adapter-link";
import { fenceCaptureLink } from "./fence-capture-link";
import { fencePresentLink } from "./fence-present-link";
import { otelBaggageLink } from "./otel-baggage-link";
import { reconnectingSubscriptionLink } from "./reconnectingSubscriptionLink";
import {
  classifySubscriptionTerminal,
  refreshSubscriptionCredentials,
} from "./subscriptionReconnectPolicy";

type GraphQLClientOptions = {
  fetch?: typeof globalThis.fetch;
};

const createHeadersLink = () =>
  new SetContextLink(async (prevContext) => {
    const locale = localStorage.getItem("i18nextLng");
    const { accessToken } = await getAccessTokenSilently();

    return {
      headers: {
        ...(prevContext.headers ?? {}),
        ...(accessToken && { authorization: `Bearer ${accessToken}` }),
        ...(locale && { "Accept-Language": locale }),
      },
    };
  });

const createClientAwarenessLink = () =>
  new ClientAwarenessLink({
    clientAwareness: {
      name: "src-web",
      version: ConfigService.APP_VERSION(),
    },
  });

const createTerminatingLink = ({ fetch }: GraphQLClientOptions = {}) =>
  new BaseHttpLink({
    uri: ConfigService.SUPERGRAPH_BASE_URL(),
    credentials: "include",
    ...(fetch && { fetch }),
  });

const createGraphQLClient = (options: GraphQLClientOptions = {}) =>
  new ApolloClient({
    link: ApolloLink.from([
      // First in the chain so each reconnect re-runs the links below it, picking
      // up whatever the header link reads at that point. The policy decides which
      // terminals are worth retrying and which of those need fresh credentials
      // first — the header link reads a cached token, which the subgraph rejects
      // once it is inside its staleness window.
      reconnectingSubscriptionLink({
        classifyTerminal: classifySubscriptionTerminal,
        onReconnect: refreshSubscriptionCredentials,
      }),
      otelBaggageLink,
      // The read-your-writes fence trio (rywf pilot FE slice): capture
      // harvests minted writeTokens and folds disclosures back into the
      // store, present attaches the live tokens to query context, and the
      // adapter transcribes them into the pilot read's `atLeast` argument
      // (the one file the F2 header lane deletes).
      fenceCaptureLink,
      fencePresentLink,
      fenceAtLeastAdapterLink,
      createHeadersLink(),
      createClientAwarenessLink(),
      createTerminatingLink(options),
    ]),
    cache: createCache(),
    incrementalHandler: new Defer20220824Handler(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
        errorPolicy: "all",
      },
    },
  });

export const GraphQLClient = createGraphQLClient();
